/**
 * Real `SenderDeps`/`SenderResumeDeps` wiring for the web app's send flow —
 * mirrors `apps/extension/src/sidepanel/filetransfer/senderDeps.ext.ts` +
 * `senderResumeDeps.ext.ts` combined into one module (no offscreen/panel
 * bundle-splitting concern applies on web — everything runs in the one
 * page). `deps.p2p` is OMITTED entirely (P2P on web is a flagged
 * fast-follow — do NOT wire P2P here; M4 v1 is relay-only) — every existing
 * client-core test still passes unmodified with `p2p` absent, and `sendFile`
 * gates every P2P attempt on `deps.p2p !== undefined`, so omitting it is
 * structurally byte-identical M2 relay behavior.
 *
 * Ctrl messages go straight through `fileCtrl.ts`'s `sendFileCtrl` — no SW
 * hop needed (web has no service-worker relay for this path; the tab must
 * stay open for a transfer to progress, documented in the UI via
 * `sendFileKeepOpenHint`).
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import {
  type AcceptDecision,
  activeSendControl,
  createOpfsSenderSource,
  detectOpfsStorage,
  hasActiveSend,
  notifyPeerProgress,
  type ResumeApi,
  requestCancel,
  requestPause,
  requestPeerError,
  resumePendingSends,
  resumeSenderTransfer,
  runWithUploadDeadline,
  type SenderDeps,
  type SenderResumeDeps,
  type SendProgressEvent,
} from "@reflecto/client-core";
import {
  decodeBase64,
  deriveSharedSecret,
  deriveTransferKey,
  encryptChunk,
  type FileCtrl,
  sha256Final,
  sha256Init,
  sha256Update,
} from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import {
  deleteTransfer as apiDeleteTransfer,
  completeTransfer,
  createTransfer,
  getTransfer,
  getTransferUploadUrl,
  markTransferPartUploaded,
  putTransferPart,
} from "../api/client";
import { sendFileCtrl } from "./fileCtrl";
import {
  getSenderState,
  listSenderStates,
  pruneSenderStates,
  removeSenderState,
  saveSenderState,
} from "./senderState";

/**
 * Pending accept/decline resolvers keyed by transferId. Registered by
 * `awaitAccept`, resolved by `notifyFileCtrlReceived` (fed from
 * `fileCtrl.ts`'s registered handler). A transferId resolves EXACTLY ONCE —
 * `notifyFileCtrlReceived` deletes the entry before resolving, so a
 * duplicate/late ctrl (a /sync replay) for an already-resolved (or
 * never-registered) transferId is silently ignored (§8.2).
 */
const pendingAccepts = new Map<
  string,
  {
    resolve: (decision: AcceptDecision) => void;
    reject: (err: Error) => void;
    /** #808 item 6 — fired once if the recipient's `offer_received` ack arrives before the decision. */
    onOfferReceived?: () => void;
  }
>();

/**
 * Feed an inbound file_ctrl into the sender's live state (docs/file-transfer-resume.md
 * §8.2). Called from the page's registered `FileCtrlHandler`.
 */
/**
 * Exhaustiveness pin for `notifyFileCtrlReceived` below. Never read at runtime
 * — the `satisfies` is the point: a 13th `FileCtrl["kind"]` becomes a COMPILE
 * ERROR here, so a new kind forces an explicit handle-or-drop decision at this
 * sender instead of falling into the `if` chain's silent default.
 */
const _senderCtrlRouting = {
  accept: "handle",
  decline: "handle",
  offer_received: "handle",
  pause: "handle",
  cancel: "handle",
  resume: "handle",
  error: "handle",
  progress: "handle",
  offer: "drop", // receiver-bound (§8.2)
  complete: "drop", // relay-only surface: the sender's own loop reports completion
  p2p_signal: "drop", // web send is relay-only — no P2P attempt to signal into
  demote: "drop", // ditto (no P2P attempt to demote)
} satisfies Record<FileCtrl["kind"], "handle" | "drop">;

export function notifyFileCtrlReceived(_senderDeviceId: string, ctrl: FileCtrl): void {
  if (ctrl.kind === "accept" || ctrl.kind === "decline") {
    const pending = pendingAccepts.get(ctrl.transferId);
    if (!pending) return;
    pendingAccepts.delete(ctrl.transferId);
    if (ctrl.kind === "decline") {
      pending.resolve({ decision: "decline" });
    } else {
      pending.resolve({ decision: "accept", resumeFromChunk: ctrl.resumeFromChunk });
    }
    return;
  }
  if (ctrl.kind === "offer_received") {
    // #808 item 6 — delivery receipt: advance the card from "delivering" to
    // "waiting-accept". Fire once; a /sync replay after the decision (entry gone)
    // or after the callback already fired is a harmless no-op.
    const pending = pendingAccepts.get(ctrl.transferId);
    const cb = pending?.onOfferReceived;
    if (pending && cb) {
      pending.onOfferReceived = undefined;
      cb();
    }
    return;
  }
  if (ctrl.kind === "pause") {
    if (hasActiveSend(ctrl.transferId)) requestPause(ctrl.transferId);
    return;
  }
  if (ctrl.kind === "cancel") {
    if (hasActiveSend(ctrl.transferId)) requestCancel(ctrl.transferId, "peer");
    return;
  }
  if (ctrl.kind === "resume") {
    void resumePausedSenderTransfer(ctrl.transferId).catch(() => {});
    return;
  }
  if (ctrl.kind === "error") {
    // §8.2 `error` row, "Active record" column: abort the loop -> P-S4 "failed" carrying the
    // ctrl's own code. Dropping it (as this surface used to) let the loop upload to completion
    // and report `done` for a file the receiver had already torn down and deleted.
    if (hasActiveSend(ctrl.transferId)) requestPeerError(ctrl.transferId, ctrl.code);
    return;
  }
  if (ctrl.kind === "progress") {
    notifyPeerProgress(ctrl.transferId, ctrl.chunksDone);
    return;
  }
  // offer/p2p_signal/demote/complete — out of scope for the sender's live-loop notifications here.
}

function awaitAccept(
  transferId: string,
  timeoutMs: number,
  onOfferReceived?: () => void
): Promise<AcceptDecision> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingAccepts.delete(transferId);
      reject(new Error("accept_timeout"));
    }, timeoutMs);
    pendingAccepts.set(transferId, {
      resolve: (decision) => {
        clearTimeout(timer);
        resolve(decision);
      },
      reject: (err) => {
        clearTimeout(timer);
        reject(err);
      },
      onOfferReceived,
    });
  });
}

/**
 * Web Locks single-writer guard (§3.1.2) — identical bridge to the
 * extension's `senderResumeDeps.ext.ts`: `navigator.locks.request` is
 * callback-scoped, so a held release-resolver per transferId keeps the lock
 * alive until `releaseLock` is called.
 */
export const heldLockReleasers = new Map<string, () => void>();

export async function acquireLock(transferId: string): Promise<boolean> {
  if (heldLockReleasers.has(transferId)) return false;
  return new Promise<boolean>((resolveAcquire) => {
    let resolveRelease: () => void = () => {};
    const released = new Promise<void>((r) => {
      resolveRelease = r;
    });
    navigator.locks.request(`reflecto-ft-${transferId}`, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        resolveAcquire(false);
        return;
      }
      heldLockReleasers.set(transferId, resolveRelease);
      resolveAcquire(true);
      await released;
    });
  });
}

export async function releaseLock(transferId: string): Promise<void> {
  const release = heldLockReleasers.get(transferId);
  if (!release) return;
  heldLockReleasers.delete(transferId);
  release();
}

/** Cross-context unpair-abort guard (§3.1.3) — web has one context, but the resume scan still needs to stop mid-loop if the user unpairs while it's running. */
let unpairedDuringScan = false;
webStorage.onChanged((changes) => {
  if ("ftSenderStates" in changes && changes.ftSenderStates?.newValue === undefined) {
    unpairedDuringScan = true;
  }
});

/**
 * Build a real `SenderDeps` for sending `file` to `recipientDeviceId`.
 * Throws `"not_paired"` if the app has no stored keys/token yet.
 */
export async function createWebSenderDeps(
  recipientDeviceId: string,
  onProgress?: (event: SendProgressEvent) => void
): Promise<SenderDeps> {
  const { token, secretKey, publicKey, deviceId, phonePubKey, phonePushCapable } =
    await webStorage.get(
      "token",
      "secretKey",
      "publicKey",
      "deviceId",
      "phonePubKey",
      // Same field slice 3 gates reply/action on. Absent means "not yet probed",
      // which `sendFiles` reads as capable — the 5-minute window, i.e. today's
      // behaviour — rather than making every first send wait half an hour.
      "phonePushCapable"
    );
  if (!token || !secretKey || !publicKey || !deviceId || !phonePubKey) {
    throw new Error("not_paired");
  }

  const secretKeyBytes = decodeBase64(secretKey);
  const senderPublicKey = decodeBase64(publicKey);
  const recipientPublicKey = decodeBase64(phonePubKey);
  const sharedSecret = await deriveSharedSecret(secretKeyBytes, recipientPublicKey);

  return {
    targetPushCapable: phonePushCapable,
    api: {
      createTransfer: (args) => createTransfer(token, args),
      uploadUrl: (transferId, partIndex) => getTransferUploadUrl(token, transferId, partIndex),
      putPart: (url, body) => runWithUploadDeadline((signal) => putTransferPart(url, body, signal)),
      markUploaded: (transferId, partIndex) =>
        markTransferPartUploaded(token, transferId, partIndex),
      complete: (transferId) => completeTransfer(token, transferId),
      deleteTransfer: (transferId) => apiDeleteTransfer(token, transferId),
    },
    sendCtrl: (ctrl) => sendFileCtrl(recipientDeviceId, ctrl),
    awaitAccept,
    crypto: { deriveTransferKey, encryptChunk },
    hash: { init: sha256Init, update: sha256Update, final: sha256Final },
    openSource: (transferId) =>
      createOpfsSenderSource(transferId, { storage: detectOpfsStorage() }),
    state: { save: (record) => saveSenderState(record) },
    sharedSecret,
    senderDeviceId: deviceId,
    senderPublicKey,
    recipientPublicKey,
    onProgress,
    control: activeSendControl,
    // p2p intentionally omitted — see module doc.
  };
}

/** Build a real `SenderResumeDeps`. Throws `"not_paired"` if the app has no stored keys/token yet. */
export async function createWebSenderResumeDeps(
  onProgress?: (event: SendProgressEvent) => void
): Promise<SenderResumeDeps> {
  const { token, secretKey, publicKey } = await webStorage.get("token", "secretKey", "publicKey");
  if (!token || !secretKey || !publicKey) {
    throw new Error("not_paired");
  }
  const secretKeyBytes = decodeBase64(secretKey);
  const senderPublicKey = decodeBase64(publicKey);
  unpairedDuringScan = false;

  const api: ResumeApi = {
    getTransfer: (transferId) => getTransfer(token, transferId),
    uploadUrl: (transferId, partIndex) => getTransferUploadUrl(token, transferId, partIndex),
    putPart: (url, body) => runWithUploadDeadline((signal) => putTransferPart(url, body, signal)),
    markUploaded: (transferId, partIndex) => markTransferPartUploaded(token, transferId, partIndex),
    complete: (transferId) => completeTransfer(token, transferId),
    deleteTransfer: (transferId) => apiDeleteTransfer(token, transferId),
  };

  return {
    listSenderStates,
    saveSenderState,
    removeSenderState,
    acquireLock,
    releaseLock,
    api,
    sendCtrl: sendFileCtrl,
    openSource: (transferId) =>
      createOpfsSenderSource(transferId, { storage: detectOpfsStorage() }),
    crypto: { deriveTransferKey, encryptChunk },
    deriveSharedSecret: (peerPub) => deriveSharedSecret(secretKeyBytes, peerPub),
    senderPublicKey,
    isAborted: () => unpairedDuringScan,
    control: activeSendControl,
    onProgress,
    onPeerProgress: onProgress
      ? (transferId, chunksDone) => onProgress({ stage: "peer-progress", transferId, chunksDone })
      : undefined,
  };
}

/** The app-open resume trigger (§5.1's "sender: on app open"). Best-effort: swallows `"not_paired"` and any other failure. */
export async function resumePendingSendsOnAppOpen(): Promise<void> {
  try {
    await pruneSenderStates();
    const deps = await createWebSenderResumeDeps();
    await resumePendingSends(deps);
  } catch {
    // Best-effort — see doc above.
  }
}

/** Run the ONE resume algorithm for a `"paused"` sender record — entry point for an inbound `resume` ctrl (a paired device's FGS-park→Resume). The local Resume affordance was removed with the manual pause/resume cut. */
export async function resumePausedSenderTransfer(
  transferId: string,
  onProgress?: (event: SendProgressEvent) => void
): Promise<void> {
  const record = await getSenderState(transferId);
  if (record?.status !== "paused") return;
  const deps = await createWebSenderResumeDeps(onProgress);
  await resumeSenderTransfer(record, deps);
}

/** Local user Cancel click — works for both an ACTIVELY uploading transfer and a `"paused"` one. */
export async function cancelActiveSend(
  transferId: string,
  onProgress?: (event: SendProgressEvent) => void
): Promise<void> {
  if (hasActiveSend(transferId)) {
    requestCancel(transferId, "local");
    return;
  }
  const record = await getSenderState(transferId);
  if (record?.status !== "paused") return;
  const { token } = await webStorage.get("token");
  await sendFileCtrl(record.peerDeviceId, { kind: "cancel", transferId, reason: "user" }).catch(
    () => {}
  );
  if (token) await apiDeleteTransfer(token, transferId).catch(() => {});
  record.status = "cancelled";
  record.updatedAt = Date.now();
  await saveSenderState(record);
  await createOpfsSenderSource(transferId, { storage: detectOpfsStorage() })
    .deleteCopy()
    .catch(() => {});
  // `paused`, NOT `transfer`: the `hasActiveSend` guard above already returned for
  // the live-loop case, and the `status !== "paused"` guard filtered the rest — so
  // reaching here means no loop was running and no bytes were in flight. Matches
  // `senderDeps.ext.ts`'s byte-identical branch; these two must not drift.
  onProgress?.({ stage: "failed", reason: "cancelled", transferId, failStage: "paused" });
}
